<template>
    <Modal
        :title="isUpdate ? $t('event.update-event') : $t('event.add-event')"
        :is-open="isOpen"
        @update:is-open="setOpen($event)"
    >
        <div class="space-y-4">
            <Tabs v-model="categorySelected" :headers="categories" />
            <div class="flex-1 space-y-2">
                <Tabs v-model="subCategorySelected" :headers="subCategories" pills />
            </div>

            <div v-if="!isSessionSubcategory && displayHorseField" class="rounded-md shadow-2xs" :class="{ disabled: horseLocked }">
                <Select v-model.number="editableEvent.horse_id" name="Horse" :label="$t('horse.title')" required>
                    <option v-for="horse in horseOptions" :key="horse.id" :value="horse.id">
                        {{ horse.name }}
                    </option>
                </Select>
            </div>

            <template v-if="!isSessionSubcategory">
                <div v-if="metaKey" class="rounded-md shadow-2xs">
                    <Select
                        v-model="editableEvent.meta"
                        :name="$t(`meta.${metaKey}.title`)"
                        :label="$t(`meta.${metaKey}.title`)"
                        required
                    >
                        <option
                            v-for="(meta, keyOfMeta) in metas[metaKey]"
                            :key="`crudEventModal-${keyOfMeta}`"
                            :value="keyOfMeta"
                        >
                            {{ $t(`meta.${metaKey}.${formatKeyOfMeta(keyOfMeta)}`) }}
                        </option>
                    </Select>
                </div>
                <div v-else-if="title.display" class="rounded-md shadow-2xs">
                    <Input
                        v-model="editableEvent.title"
                        :placeholder="$t(`event.subcategory.placeholder-${title.label}`)"
                        :type="title.inputType"
                        :label="$t(`event.subcategory.label-${title.label}`)"
                        required
                        :unit="subCategories[subCategorySelected].unit"
                        :userUnit="subCategories[subCategorySelected].userUnit"
                    />
                </div>

                <div class="rounded-md shadow-2xs">
                    <Input v-model="editableEvent.start_at" type="date" :label="$t('event.date')" required />
                </div>

                <div class="rounded-md shadow-2xs">
                    <Input
                        v-model="editableEvent.description"
                        :placeholder="$t('event.add-description')"
                        type="textarea"
                        :label="$t('event.description')"
                    />
                </div>
            </template>

            <template v-else>
                <SessionForm
                    :modelValue="sessionForm"
                    @update:modelValue="updateSessionForm"
                    :metas="metas"
                    :horses="horseOptions"
                    :riders="riders"
                    :userUnits="userUnits"
                />
            </template>
        </div>

        <div class="mt-5 rounded-md shadow-2xs">
            <div v-if="isUpdate" class="space-y-4">
                <Button :action="() => update(editableEvent)">
                    {{ $t('form.save') }}
                </Button>

                <Button :action="() => remove(editableEvent.id)" danger>
                    {{ $t('form.delete') }}
                </Button>
            </div>

            <Button v-else :action="() => create(editableEvent)">
                {{ $t('form.save') }}
            </Button>
        </div>
    </Modal>
</template>

<script setup>
import { computed, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { getUnitKey } from '@/lib/helpers/convert'

import useEvents from '@/lib/hooks/use-events'
import useMetas from '@/lib/hooks/use-metas'
import useHorses from '@/lib/hooks/use-horses'
import { activeHorsesOf } from '@/lib/horses/archived'
import useUser from '@/lib/hooks/use-users'
import useSessions from '@/lib/hooks/use-sessions'
import useRiders from '@/lib/hooks/use-riders'
import { firstMetaKey } from '@/lib/meta-default'
import { useRoute } from 'vue-router'

import { useToast } from 'primevue/usetoast'
import Button from '@/components/buttons/Primary.vue'
import Input from '@/components/form/Input.vue'
import Select from '@/components/form/Select.vue'
import Tabs from '@/components/Tabs.vue'
import Modal from '@/components/modals/Modal.vue'
import SessionForm from '@/components/sessions/SessionForm.vue'

const props = defineProps({
    isOpen: { type: Boolean, default: false },
    event: { type: Object, required: true },
    defaultCategory: { type: String, default: null },
    defaultSubCategory: { type: String, default: null },
})

const emits = defineEmits(['update:isOpen'])

const { t } = useI18n()
const toast = useToast()
const { createEvent, updateEvent, deleteEvent } = useEvents()
const { metas, getEventTypeByKey } = useMetas()
const { horses, activeHorses, mutateHorse } = useHorses()
const { riders } = useRiders()
const { userUnits, refreshUser } = useUser()
const { mutateSession, createSession } = useSessions()
const route = useRoute()

const editableEvent = ref({})
const horseLocked = ref(false)
const sessionForm = reactive({
    horse_id: null,
    rider_id: null,
    start_at: '',
    name: '',
    description: '',
    discipline: null,
    type: null,
    indoor: 0,
    city: '',
    distance: null,
    elapsed_time: 0,
    walk_time: 0,
    trot_time: 0,
    canter_time: 0,
    standing_time: 0,
    walk_distance: 0,
    trot_distance: 0,
    canter_distance: 0,
    standing_distance: 0,
})

const updateSessionForm = (val) => {
    Object.assign(sessionForm, val || {})
}

const isUpdate = computed(() => 'id' in editableEvent.value)
// The event's own horse (an edited event, or one added from that horse's page) stays
// listed even archived; otherwise only active horses are offered.
const horseOptions = computed(() => activeHorsesOf(horses.value, editableEvent.value?.horse_id))
const eventCategories = computed(() => metas.value?.['event_category'] ?? [])

const displayHorseField = ref(true)
const title = ref({
    label: 'value',
    inputType: 'number',
    display: true,
})
const metaKey = ref(null)

const categorySelected = ref(props.defaultCategory)
const categories = computed(() => {
    let result = {}
    for (let indexCategory = 0; indexCategory < eventCategories.value?.length; indexCategory++) {
        const categoryName = eventCategories.value[indexCategory]?.name
        result[categoryName] = {
            text: t('event.category.' + categoryName),
            disable: isUpdate.value,
        }
    }
    return result
})
//in order to auto select the first category on the first load
watch(
    () => eventCategories.value,
    () => {
        if (eventCategories.value?.length && !categorySelected.value) {
            categorySelected.value = eventCategories.value[0]?.name
        }
    },
    { immediate: true }
)

const subCategoriesOfCategory = computed(
    () => eventCategories.value?.find((category) => category.name === categorySelected.value)?.subcategories
)

const subCategorySelected = ref(props.defaultSubCategory)
const subCategories = computed(() => {
    let result = {}
    for (let indexSubcategory = 0; indexSubcategory < subCategoriesOfCategory.value?.length; indexSubcategory++) {
        const eventType = subCategoriesOfCategory.value[indexSubcategory]
        result[eventType.subcategory_name] = {
            text: t('event.subcategory.' + eventType.subcategory_name),
            unit: eventType.unit,
            userUnit: userUnits.value?.[getUnitKey(eventType.unit)],
            event_type_id: eventType.event_type_id,
            disable: isUpdate.value,
        }
    }
    return result
})

const isSessionSubcategory = computed(() => subCategorySelected.value === 'session')

const firstMetaIndex = (metaKey) => {
    const keys = metas.value && metas.value[metaKey] ? Object.keys(metas.value[metaKey]) : []
    return keys.length ? parseInt(keys[0]) : null
}

watch(
    () => props.event,
    () => {
        editableEvent.value = props.event
        if (editableEvent.value?.horse?.id) {
            editableEvent.value.horse_id = editableEvent.value?.horse?.id
            horseLocked.value = true
        } else horseLocked.value = false
        if (isSessionSubcategory.value) {
            resetSessionFields()
        }
    },
    { immediate: true }
)

// To display right subcategories according to the category selected
watch(
    () => subCategories.value,
    () => {
        if (!subCategoriesOfCategory.value?.find((subcat) => subcat.subcategory_name === subCategorySelected.value)) {
            subCategorySelected.value = subCategoriesOfCategory.value?.[0]?.subcategory_name
        }
    },
    { immediate: true }
)
//when changing the event without closing the modal ?
watch(
    () => editableEvent.value?.event_type_id,
    () => {
        const eventType = getEventTypeByKey(editableEvent.value?.event_type_id, 'event_type_id')
        if (eventType) {
            categorySelected.value = eventType?.category_name
            subCategorySelected.value = eventType?.subcategory_name
        }
    },
    { immediate: true }
)
// To display right form fields according to the subcategory selected
watch(
    () => subCategorySelected.value,
    () => {
        const eventType = getEventTypeByKey(subCategorySelected.value, 'subcategory_name')
        displayHorseField.value = eventType?.have_horse
        if (isSessionSubcategory.value) {
            displayHorseField.value = false
            resetSessionFields()
        }
        metaKey.value = eventType?.meta_key

        title.value.display = eventType?.title_type !== 'hidden'

        if (['integer', 'float'].includes(eventType?.title_type)) {
            title.value.label = 'value'
            title.value.inputType = 'number'
        } else {
            title.value.label = 'title'
            title.value.inputType = 'text'
        }
    },
    { immediate: true }
)

watch(
    () => activeHorses.value,
    () => {
        if (activeHorses.value?.length && !editableEvent.value.horse_id)
            editableEvent.value.horse_id = activeHorses.value?.[0]?.id
        if (isSessionSubcategory.value && activeHorses.value?.length && sessionForm.horse_id === undefined) {
            sessionForm.horse_id = activeHorses.value[0]?.id
        }
    },
    { immediate: true }
)

watch(
    () => riders.value,
    () => {
        if (isSessionSubcategory.value && riders.value?.length && sessionForm.rider_id === undefined) {
            sessionForm.rider_id = riders.value[0]?.id
        }
    },
    { immediate: true }
)

watch(
    () => metas.value,
    () => {
        if (isSessionSubcategory.value) {
            if (sessionForm.discipline === undefined && metas.value?.['session-discipline']) {
                sessionForm.discipline = firstMetaKey(metas.value, 'session-discipline')
            }
            if (sessionForm.type === undefined && metas.value?.['session-type']) {
                sessionForm.type = firstMetaIndex('session-type')
            }
        }
    },
    { immediate: true }
)

watch(
    () => isSessionSubcategory.value,
    (isSession) => {
        if (isSession) {
            resetSessionFields()
        }
    },
    { immediate: true }
)

watch(
    () => props.isOpen,
    (isOpen) => {
        if (isOpen && isSessionSubcategory.value) {
            resetSessionFields()
        }
    }
)

const setOpen = (state) => {
    if (state === true) {
        refreshUser()
    }
    emits('update:isOpen', state)
}

const create = (event) => {
    if (isSessionSubcategory.value) {
        const payload = formatSessionPayload()
        createSession(payload)
            .then((response) => {
                toast.add({
                    severity: 'success',
                    summary: response?.message ?? 'Session created',
                    life: 3000,
                })
                setOpen(false)
                if (response?.id) {
                    mutateSession(response.id)
                }
            })
            .catch((error) => {
                toast.add({ severity: 'warn', summary: error?.message, life: 3000 })
            })
        return
    }

    const formatedEvent = formatPayload(event)

    createEvent(formatedEvent)
        .then((response) => {
            toast.add({ severity: 'success', summary: response?.data?.message ?? t('event.event-created'), life: 3000 })
            setOpen(false)
            if (route.params.sessionId) {
                mutateSession(route.params.sessionId)
            } else if (route.params.horse) {
                mutateHorse(route.params.horse)
            }
        })
        .catch((error) => {
            toast.add({ severity: 'warn', summary: error?.message, life: 3000 })
        })
}

const update = (event) => {
    const formatedEvent = formatPayload(event)

    updateEvent(formatedEvent.id, formatedEvent)
        .then((response) => {
            toast.add({ severity: 'success', summary: response?.data?.message ?? t('event.event-updated'), life: 3000 })
            setOpen(false)
        })
        .catch((error) => {
            toast.add({ severity: 'warn', summary: error?.message, life: 3000 })
        })
}

const remove = (eventId) => {
    deleteEvent(eventId)
        .then((response) => {
            toast.add({ severity: 'success', summary: response?.data?.message ?? t('event.event-deleted'), life: 3000 })
            setOpen(false)
        })
        .catch((error) => {
            toast.add({ severity: 'warn', summary: error?.message, life: 3000 })
        })
}

const formatPayload = (event) => {
    event.event_type_id = subCategories.value[subCategorySelected.value].event_type_id
    event.end_at = event.start_at

    if (!title.value.display) event.title = null
    if (metaKey.value) event.title = event.meta //because the title is a translated version of the meta

    return event
}

const formatKeyOfMeta = (keyOfMeta) => {
    if (keyOfMeta && keyOfMeta?.toLowerCase()) {
        return keyOfMeta?.toLowerCase()?.replaceAll(' ', '-')
    }
    return ''
}

const defaultStartAt = () => {
    const now = new Date()
    now.setSeconds(0, 0)
    return now.toISOString().slice(0, 16)
}

const buildSessionStartAt = () => {
    const startAt = editableEvent.value?.start_at
    if (startAt && /^\d{4}-\d{2}-\d{2}$/.test(startAt)) {
        return `${startAt}T12:00`
    }
    if (startAt) {
        return startAt.slice(0, 16)
    }
    return defaultStartAt()
}

const resetSessionFields = () => {
    const defaultHorse =
        editableEvent.value?.horse_id ??
        editableEvent.value?.horse?.id ??
        (activeHorses.value?.length ? activeHorses.value[0].id : null)
    const defaultRider = editableEvent.value?.rider?.id ?? (riders.value?.length ? riders.value[0].id : null)

    Object.assign(sessionForm, {
        horse_id: defaultHorse,
        rider_id: defaultRider,
        start_at: buildSessionStartAt(),
        name: editableEvent.value?.title || '',
        description: editableEvent.value?.description || '',
        discipline: firstMetaKey(metas.value, 'session-discipline'),
        type: firstMetaIndex('session-type'),
        indoor: 0,
        city: '',
        distance: 0,
        elapsed_time: 0,
        walk_time: 0,
        trot_time: 0,
        canter_time: 0,
        standing_time: 0,
        walk_distance: 0,
        trot_distance: 0,
        canter_distance: 0,
        standing_distance: 0,
    })
}

const formatSessionPayload = () => {
    const payload = { ...sessionForm }

    const durationSeconds =
        Number.isFinite(Number(payload.elapsed_time)) && Number(payload.elapsed_time) >= 0
            ? Math.round(Number(payload.elapsed_time))
            : 0
    const gaitTotal =
        Math.max(0, Math.round(payload.walk_time || 0)) +
        Math.max(0, Math.round(payload.trot_time || 0)) +
        Math.max(0, Math.round(payload.canter_time || 0)) +
        Math.max(0, Math.round(payload.standing_time || 0))

    payload.walk_time = Math.max(0, Math.round(payload.walk_time || 0))
    payload.trot_time = Math.max(0, Math.round(payload.trot_time || 0))
    payload.canter_time = Math.max(0, Math.round(payload.canter_time || 0))
    payload.standing_time = Math.max(0, Math.round(payload.standing_time || 0))
    payload.elapsed_time = gaitTotal > 0 ? gaitTotal : durationSeconds

    payload.walk_distance = Math.max(0, Math.round(Number(payload.walk_distance) || 0))
    payload.trot_distance = Math.max(0, Math.round(Number(payload.trot_distance) || 0))
    payload.canter_distance = Math.max(0, Math.round(Number(payload.canter_distance) || 0))
    payload.standing_distance = Math.max(0, Math.round(Number(payload.standing_distance) || 0))

    const gaitDistanceTotal =
        payload.walk_distance + payload.trot_distance + payload.canter_distance + payload.standing_distance

    if (payload.start_at) {
        const startDate = new Date(payload.start_at)
        payload.start_at = startDate.toISOString()

        if (!Number.isNaN(payload.elapsed_time) && payload.elapsed_time > 0) {
            const endDate = new Date(startDate)
            endDate.setSeconds(endDate.getSeconds() + payload.elapsed_time)
            payload.end_at = endDate.toISOString()
        }
    }

    if (payload.distance !== null && payload.distance !== undefined) {
        payload.distance = Math.max(0, Math.round(Number(payload.distance)))
    } else if (gaitDistanceTotal > 0) {
        payload.distance = gaitDistanceTotal
    }

    return payload
}
</script>
